<script setup>
/**
 * Send QR Notices — port of AddQRNotices.vue.
 * Note: requires `xlsx` and `qrcode` npm packages for full functionality.
 * Without them the form works but QR compositing is skipped (content is sent as-is).
 */
import { ref, onMounted } from "vue";
import { useRouter } from "vue-router";
import UploadField from "@/components/UploadField.vue";
import RichTextField from "@/components/RichTextField.vue";
import { uploadImage, uploadVideo, uploadFile } from "@/lib/uploads";
import { fetchUsers } from "@/features/users/api";
import { addMessage, sendGuidNotification } from "./api";
import { useTemplates } from "./useTemplates";

const DEFAULT_IMG = "https://firebasestorage.googleapis.com/v0/b/aspial.appspot.com/o/announcements%2Faspial.jpg?alt=media&token=ea877aa0-2ec4-4caa-9342-3280ceafb2fc";
const BTN_LINK_ITEMS = ["Kronos", "IHP", "Success Factor", "Concur", "Zendesk", "Campaign", "Videos", "More Options"];

const router = useRouter();
const loading = ref(false), success = ref(false), error = ref(""), validationError = ref("");
const excelFileName = ref(""), excelRows = ref([]);
const coverImageUrl = ref(DEFAULT_IMG);
const users = ref([]);
const progressOpen = ref(false), processingCount = ref(0), totalCount = ref(0), processingDone = ref(false), skippedList = ref([]);

const form = ref({ notif_title: "", notif_content: "", title: "", content: "", startDate: "", endDate: "", btnTitle: "", btnLink: "", redirectUrl: "", isExternalBrowser: false, sendNotif: true });

const imageUploader = (file, p) => uploadImage("messages", file, p);
const redirectUploader = (file, p) => {
  if (file.type.includes("image")) return uploadImage("messages", file, p);
  if (file.type.includes("video")) return uploadVideo(file, p, "messages");
  return uploadFile("messages", file, p);
};

onMounted(async () => {
  try { users.value = await fetchUsers() || []; } catch { /* non-fatal */ }
});

// Parse CSV/XLSX file
async function handleFile(e) {
  const file = e.target.files?.[0]; e.target.value = "";
  if (!file) return;
  error.value = "";
  try {
    // Try xlsx dynamic import, fall back to CSV parse
    if (file.name.endsWith(".csv")) {
      const text = await file.text();
      const lines = text.trim().split(/\r?\n/);
      const headers = lines[0].split(",").map(h => h.replace(/^\uFEFF/, "").trim().toLowerCase().replace(/[^a-z0-9]/g, ""));
      const empIdx = headers.indexOf("employeeid");
      const qrIdx = headers.indexOf("qrcode");
      if (empIdx === -1 || qrIdx === -1) { error.value = 'CSV must have columns "employee_id" and "qr_code".'; return; }
      excelRows.value = lines.slice(1).map(line => { const v = line.split(","); return { employee_id: (v[empIdx] ?? "").trim(), qr_code: (v[qrIdx] ?? "").trim() }; }).filter(r => r.employee_id && r.qr_code);
    } else {
      const { read, utils } = await import("xlsx").catch(() => { throw new Error("xlsx package not installed. Please use CSV format instead."); });
      const buf = await file.arrayBuffer();
      const wb = read(buf);
      const rows = utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]]);
      excelRows.value = rows.map(r => ({ employee_id: String(r["employee_id"] ?? r["Employee ID"] ?? "").trim(), qr_code: String(r["qr_code"] ?? r["QR Code"] ?? "").trim() })).filter(r => r.employee_id && r.qr_code);
    }
    excelFileName.value = file.name;
  } catch (err) { error.value = err.message; excelFileName.value = ""; excelRows.value = []; }
}

async function submit() {
  error.value = ""; validationError.value = "";
  if (!excelRows.value.length) { validationError.value = "Upload an Excel/CSV file with employee_id and qr_code columns."; return; }
  if (!form.value.title.trim()) { validationError.value = "Inbox Title is required."; return; }
  if (!form.value.startDate || !form.value.endDate) { validationError.value = "Start and end dates are required."; return; }

  loading.value = true; progressOpen.value = true; processingCount.value = 0; totalCount.value = excelRows.value.length; processingDone.value = false; skippedList.value = [];

  const byEmpId = new Map(users.value.map(u => [String(u.customer_id ?? "").trim(), u]));

  for (const row of excelRows.value) {
    processingCount.value++;
    const user = byEmpId.get(row.employee_id);
    if (!user) { skippedList.value.push(row.employee_id); continue; }
    const guid = user.guid ?? user._id ?? user.objectID;

    // Content with QR info (plain text since qrcode library not installed)
    const contentWithQR = form.value.content + `\n<p style="font-family:monospace;text-align:center;font-size:14px">QR: ${row.qr_code}</p>`;

    try {
      const payload = {
        imgUrl: coverImageUrl.value,
        notif_title: form.value.notif_title,
        notif_content: form.value.notif_content,
        title: form.value.title,
        content: contentWithQR,
        startDate: new Date(form.value.startDate),
        endDate: new Date(form.value.endDate),
        btnTitle: form.value.btnTitle,
        btnLink: form.value.btnLink,
        redirectUrl: form.value.redirectUrl,
        isExternalBrowser: form.value.isExternalBrowser,
        userGuids: [guid],
        msgContent: { 0: [guid] },
      };
      const result = await addMessage(payload);
      if (form.value.sendNotif) {
        await sendGuidNotification({ notif_title: form.value.notif_title, notif_content: form.value.notif_content, title: form.value.title, bodyMessage: contentWithQR, userGuids: [guid], startDate: form.value.startDate, endDate: form.value.endDate, objectId: result?._id ?? result }).catch(() => {});
      }
    } catch { skippedList.value.push(row.employee_id); }
  }

  processingDone.value = true; loading.value = false;
  if (!skippedList.value.length) success.value = true;
}

const { templates, templatesOpen, templatesLoading, openTemplates, closeTemplates } = useTemplates(msg => { error.value = msg; });
function applyTemplate(t) {
  coverImageUrl.value = t.imgUrl || DEFAULT_IMG;
  form.value.notif_title = t.notif_title ?? ""; form.value.notif_content = t.notif_content ?? ""; form.value.title = t.title ?? ""; form.value.content = t.content ?? ""; form.value.btnTitle = t.btnTitle ?? ""; form.value.btnLink = t.btnLink ?? ""; form.value.redirectUrl = t.redirectUrl ?? ""; form.value.isExternalBrowser = !!t.isExternalBrowser;
  closeTemplates();
}
</script>

<template>
  <section>
    <VBtn variant="text" prepend-icon="mdi:arrow-left" class="mb-4" @click="router.push({ name: 'messages' })">Back</VBtn>
    <div class="d-flex align-center justify-space-between mb-4">
      <h1 class="text-h5">Send QR Notices</h1>
      <VBtn variant="tonal" @click="openTemplates">Use Template</VBtn>
    </div>
    <VCard class="pa-4" max-width="820">
      <VAlert v-if="error" type="error" class="mb-4">{{ error }}</VAlert>
      <VAlert v-if="success" type="success" class="mb-4">All notices sent successfully.</VAlert>
      <form @submit.prevent="submit">
        <div class="mb-4">
          <div class="text-body-2 font-weight-semibold mb-1">Excel / CSV File (columns: employee_id, qr_code)</div>
          <input type="file" accept=".csv,.xlsx,.xls" @change="handleFile" />
          <p v-if="excelFileName" class="text-caption text-medium-emphasis mt-1">{{ excelFileName }} — {{ excelRows.length }} row(s)</p>
        </div>
        <UploadField v-model="coverImageUrl" kind="image" label="Cover Photo" :uploader="imageUploader" class="mb-4" />
        <VRow>
          <VCol cols="12" md="6"><VTextField v-model="form.startDate" label="Start Date" type="datetime-local" /></VCol>
          <VCol cols="12" md="6"><VTextField v-model="form.endDate" label="End Date" type="datetime-local" /></VCol>
          <VCol cols="12"><VTextField v-model="form.notif_title" label="Notification Title" /></VCol>
          <VCol cols="12"><VTextField v-model="form.notif_content" label="Notification Content" /></VCol>
          <VCol cols="12"><VTextField v-model="form.title" label="Inbox Title" /></VCol>
          <VCol cols="12"><RichTextField v-model="form.content" label="Inbox Content" :image-uploader="imageUploader" /></VCol>
          <VCol cols="12" md="6"><VTextField v-model="form.btnTitle" label="Button Title" /></VCol>
          <VCol cols="12" md="6">
            <VAutocomplete v-model="form.btnLink" label="Button Link" :items="BTN_LINK_ITEMS" clearable placeholder="—" auto-select-first />
          </VCol>
          <VCol v-if="!form.btnLink" cols="12"><VTextField v-model="form.redirectUrl" label="Button URL" /></VCol>
          <VCol v-if="!form.btnLink" cols="12">
            <UploadField v-model="form.redirectUrl" kind="file" label="Upload PDF / Image for button" accept="image/*,application/pdf,video/*" :uploader="redirectUploader" />
          </VCol>
          <VCol cols="6"><VSwitch v-model="form.isExternalBrowser" label="External Browser" hide-details /></VCol>
          <VCol cols="12"><VCheckbox v-model="form.sendNotif" label="Send push notification" hide-details /></VCol>
        </VRow>
        <VAlert v-if="validationError" type="warning" class="mb-4 mt-2">{{ validationError }}</VAlert>
        <VBtn type="submit" :loading="loading" class="mt-4">Send Notices</VBtn>
      </form>
    </VCard>

    <!-- Progress dialog -->
    <VDialog v-model="progressOpen" max-width="400" persistent>
      <VCard title="Sending Notices">
        <VCardText>
          <VProgressLinear :model-value="totalCount ? (processingCount / totalCount) * 100 : 0" color="primary" rounded height="8" class="mb-3" />
          <p>{{ processingCount }} / {{ totalCount }}</p>
          <template v-if="processingDone">
            <p v-if="skippedList.length" class="text-warning">{{ skippedList.length }} employees not found or errored: {{ skippedList.slice(0, 5).join(", ") }}{{ skippedList.length > 5 ? '…' : '' }}</p>
            <p v-else class="text-success">All sent successfully!</p>
          </template>
        </VCardText>
        <VCardActions v-if="processingDone" class="justify-end px-4 pb-4">
          <VBtn @click="progressOpen = false; if(success) router.push({ name: 'messages' })">Close</VBtn>
        </VCardActions>
      </VCard>
    </VDialog>

    <!-- Templates dialog -->
    <VDialog v-model="templatesOpen" max-width="480">
      <VCard title="Use a Template">
        <VCardText>
          <p v-if="templatesLoading">Loading…</p>
          <p v-else-if="!templates.length" class="text-medium-emphasis">No templates.</p>
          <VList v-else><VListItem v-for="t in templates" :key="t._id" :title="t.title || 'Untitled'"><template #append><VBtn size="small" @click="applyTemplate(t)">Use</VBtn></template></VListItem></VList>
        </VCardText>
        <VCardActions class="justify-end px-4 pb-4"><VBtn variant="tonal" @click="closeTemplates">Close</VBtn></VCardActions>
      </VCard>
    </VDialog>
  </section>
</template>
